<template>
  <section
    :class="[
      'tw-flex tw-flex-col tw-justify-center tw-items-center tw-bg-ap-nero',
    ]"
  >
    <HelloThere @click-continue="onClickContinueButton" />
    <div
      :class="[
        'apDesktop:tw-text-label-md-desktop tw-text-label-md-mobile tw-text-ap-nobel',
        'tw-flex tw-flex-col apDesktop:tw-pt-[160px] tw-pt-[64px] tw-pb-[64px]',
        'apDesktop:tw-max-w-[1304px] apTablet:tw-max-w-[584px]',
        'tw-w-full apDesktop:tw-px-[80px] apTablet:tw-px-0 tw-px-6',
      ]"
    >
      <div
        :class="[
          'tw-w-full tw-grid tw-grid-cols-1 apTablet:tw-grid-cols-2 apDesktop:tw-grid-cols-4',
          'tw-gap-y-8',
        ]"
      >
        <!-- Expertise Section -->
        <section class="tw-flex tw-flex-col tw-gap-2 tw-min-w-[304px]">
          <div :class="titleStyle">
            EXPERTISE
          </div>
          <ol>
            <li
              v-for="(item, index) in expertises"
              :key="index"
              class="tw-cursor-pointer hover:tw-underline"
              @click="handleExpertiseOnClick(item)"
            >
              {{ item.title }}
            </li>
          </ol>
        </section>

        <!-- Work Section -->
        <section class="tw-flex tw-flex-col tw-gap-2 tw-min-w-[304px]">
          <div :class="titleStyle">
            WORK
          </div>
          <ol>
            <li
              v-for="(item, index) in works.filter((item) => item.overview.show_in_footer)"
              :key="index"
              class="tw-cursor-pointer hover:tw-underline"
              @click="handleWorkOnClick(item)"
            >
              {{ item.title }}
            </li>
          </ol>
        </section>

        <!-- About Section -->
        <section class="tw-flex tw-flex-col tw-gap-2 tw-min-w-[304px]">
          <div :class="titleStyle">
            ABOUT
          </div>
          <ol>
            <li class="tw-cursor-pointer hover:tw-underline" @click="jumpToId('about-us')">
              About NEXT
            </li>
          </ol>
        </section>
        <section class="tw-flex tw-flex-col tw-gap-2 tw-min-w-[304px]">
          <div :class="titleStyle">
            CONTACT
          </div>
          <ol>
            <li>
              <button @click="onClickContinueButton()">
                Get in touch
              </button>
            </li>
            <li><a class="!tw-text-ap-nobel hover:tw-underline" href="mailto:hello@the-next.co">hello@the-next.co</a>
            </li>
          </ol>
        </section>
      </div>
      <hr class="tw-my-8 tw-border-ap-nobel tw-w-full">

      <!-- Address -->
      <div
        class="tw-w-full tw-flex tw-flex-col apTablet:tw-flex-row tw-justify-start apTablet:tw-justify-between tw-gap-y-8"
      >
        <section class="tw-flex tw-flex-col tw-gap-2 tw-min-w-[304px]">
          <div :class="titleStyle">
            SINGAPORE
          </div>
          <ol>
            <li>231 Mountbatten Road</li>
            <li>Block C Level 3</li>
            <li>Singapore 397999.</li>
          </ol>
        </section>
        <section class="tw-flex tw-flex-col tw-gap-2 tw-min-w-[304px] apTablet:tw-text-end">
          <ol>
            <li>© 2025 The Next Experience Pte Ltd. All Rights Reserved.</li>
            <li>
              <button @click="openPrivacyPolicyModal">
                Privacy Policy
              </button> |
              <button @click="openCookiePolicyModal">
                Cookie Policy
              </button>
            </li>
          </ol>
        </section>
      </div>
    </div>

    <!-- Contact Us Holder -->
    <ContactUsHolder v-if="showContactUsForm" :contact-us="contactUs" @close-form="showContactUsForm = false" />

    <!-- Expertise Modal -->
    <TeleportModal v-model:visible="modalExpertiseOpened" :fit-content="true">
      <CardModelContent
        :text="modalExpertiseData!.title"
        :tagline="modalExpertiseData!.tagline"
        :description="modalExpertiseData!.description"
        :hyperlink="modalExpertiseData!.url"
      />
    </TeleportModal>

    <!-- Work Modal -->
    <TeleportModal v-if="modalWorkOpened" v-model:visible="modalWorkOpened">
      <div class="tw-flex tw-flex-col tw-overflow-y-auto scrollbar-hide apDesktop:tw-px-20 apTablet:tw-px-16 tw-px-6 tw-pb-8 tw-w-full">
        <WorkModalContent :data="modalWorkData" />
      </div>
    </TeleportModal>

    <!-- Privacy Policy Modal -->
    <TeleportModal v-model:visible="modalPrivacyPolicyOpened">
      <PrivacyPolicy @show-cookie-policy="openCookiePolicyModal" />
    </TeleportModal>

    <!-- Cookie Policy Modal -->
    <TeleportModal v-model:visible="modalCookiePolicyOpened">
      <CookiePolicy />
    </TeleportModal>
  </section>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { ContactUs } from '../../model/interfaces/ContactUs';
import { Expertise } from '../../model/interfaces/interfaces';
import { Work } from '../../model/interfaces/WorkInterface';
import ContactUsHolder from '../contact-us/ContactUsHolder.vue';
import CookiePolicy from '../cookie-policy/CookiePolicy.vue';
import CardModelContent from '../home/card-modal-content.vue';
import WorkModalContent from '../home/work-modal-content.vue';
import PrivacyPolicy from '../privacy-policy/PrivacyPolicy.vue';
import HelloThere from './HelloThere.vue';
import TeleportModal from './TeleportModal.vue';


const showContactUsForm = ref(false);
const contactUs = ref();

function onClickContinueButton(data?: ContactUs) {
  contactUs.value = data;
  showContactUsForm.value = true;
}

const titleStyle =
  'tw-text-label-bold-mobile apTablet2:tw-text-label-bold-desktop';

const props = defineProps({
  expertises: {
    type: Array<Expertise>,
    required: false,
    default: () => []
  },
  works: {
    type: Array<Work>,
    required: false,
    default: () => []
  },
  showCookiePolicy: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:showCookiePolicy']);

watch(() => props.showCookiePolicy, (value) => {
  if (value) {
    openCookiePolicyModal();
  }
});

function jumpToId(id: string) {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
}

/**
 * Handle Expertise onClick
 */
const modalExpertiseOpened = ref(false);
const modalExpertiseData = ref<Expertise>();
function handleExpertiseOnClick(item: Expertise) {
  modalExpertiseData.value = item;
  if (modalExpertiseData.value === undefined) {
    return;
  }
  // Open modal
  document.body.style.overflow = 'hidden';
  modalExpertiseOpened.value = true;
}

/**
 * Handle Work onClick
 */
const modalWorkOpened = ref(false);
const modalWorkData = ref();
function handleWorkOnClick(item: Work) {
  modalWorkData.value = item;
  if (modalWorkData.value === undefined) {
    return;
  }
  // Open modal
  document.body.style.overflow = 'hidden';
  modalWorkOpened.value = true;
}

/**
 * Modal Privacy Policy
 */
const modalPrivacyPolicyOpened = ref(false);

function openPrivacyPolicyModal() {
  modalCookiePolicyOpened.value = false;
  modalPrivacyPolicyOpened.value = true;
  document.body.style.overflow = 'hidden';
}

/**
 * Modal Cookie Policy
 */
const modalCookiePolicyOpened = ref(false);

function openCookiePolicyModal() {
  modalPrivacyPolicyOpened.value = false;
  modalCookiePolicyOpened.value = true;
  document.body.style.overflow = 'hidden';
}
</script>

<style lang="scss" scoped>
button:hover {
  text-decoration: underline;
}
</style>
